import { buildTerrain, parcelFrame } from "./sceneRows";
import { deriveField } from "./fieldGeometry";
import { siteGeometryErrors } from "./siteGeometry";
import { axisTiltFor, crossAxisSlopeFor } from "./rowShading";

export function resolveSceneModel({
  latitude,
  totalModules,
  longitude,
  surfaceTilt,
  surfaceAzimuth,
  pitch,
  rowVerticalModules,
  moduleLength,
  moduleWidth,
  groundClearance,
  fieldRows,
  rowModules,
  parcelTables,
  parcelOutline,
  arrayOffsetEast = 0,
  arrayOffsetNorth = 0,
  slopeTilt,
  slopeAzimuth,
  portrait,
  halfCut,
  bypassDiodes,
  nearShadingModel,
  electricalEffect,
  mountType,
  axisOrientation,
  maxAngle,
  backTrack,
  installationType,
  siteGeometry,
  roofPitch = 0,
  roofAzimuth = 180,
}) {
    const p = Number(pitch);
    const nV = Math.max(1, Math.trunc(Number(rowVerticalModules) || 0));
    const modLen = Number(moduleLength);
    const modWid = Number(moduleWidth);
    if (!(p > 0) || !(modLen > 0) || !(nV >= 1)) return null;

    // Slant height of one row, and the dimension that runs ALONG it.
    //
    // In portrait a module stands on its short edge: the length climbs the
    // slope and the width runs along the row. In landscape the two swap. The
    // engine resolves it this way (Site_spacing_info: mod_along_slope =
    // length in portrait, width in landscape) and the viewer did not - so a
    // landscape array was drawn with a row 2.28 m tall instead of 1.13 m, at
    // twice the GCR, and shaded accordingly. The picture, not the engine, was
    // the one that was wrong.
    const upright = portrait !== false;
    const alongSlope = upright ? modLen : (modWid > 0 ? modWid : modLen);
    const alongRow = upright ? (modWid > 0 ? modWid : 1.1) : modLen;
    const collectorWidth = alongSlope * nV;
    const gcr = collectorWidth / p;

    const tracker = String(mountType || "fixed").toLowerCase() !== "fixed";
    const axisAzimuth = tracker
      ? String(axisOrientation || "North-South").toLowerCase().startsWith("north")
        ? 180
        : 90
      : Number(surfaceAzimuth ?? 180) - 90;
    const onRoof = String(installationType || "").startsWith("roof");
    // Other roof faces and obstacles are edited independently. A temporary
    // error in one of them must not replace this PV face's support plane with
    // the default flat roof: existing module offsets would then be applied to
    // a different grid and the array would appear pulled apart.
    const roof = onRoof ? (siteGeometry?.roof && !siteGeometryErrors({ roof: siteGeometry.roof }).length
      ? siteGeometry.roof : { pitchDeg: roofPitch, azimuthDeg: roofAzimuth }) : null;
    const supportTilt = roof ? roof.pitchDeg : slopeTilt;
    const supportAzimuth = roof ? roof.azimuthDeg : slopeAzimuth;

    return {
      lat: Number(latitude),
      lon: Number(longitude),
      pitch: p,
      collectorWidth,
      gcr,
      verticalModules: nV,
      // The along-the-row dimension, whichever module edge that is.
      moduleWidth: alongRow,
      // Absence is not zero: Number(null) is 0 and finite, so an unset
      // clearance came through as a row lying on the ground.
      clearance:
        groundClearance != null && Number.isFinite(Number(groundClearance))
          ? Number(groundClearance)
          : 0.5,
      surfaceTilt: Number(surfaceTilt ?? 25),
      surfaceAzimuth: Number(surfaceAzimuth ?? 180),
      axisAzimuth,
      tracker,
      maxAngle: Number(maxAngle ?? 60),
      backtrack: Boolean(backTrack),
      roof,
      emptyRoof: onRoof && Number(totalModules) === 0,
      siteGeometry,
      slopeTilt: Number(supportTilt) || 0,
      slopeAzimuth: supportAzimuth == null ? null : Number(supportAzimuth),
      // Ground slope resolved into the two components the row-shading model
      // takes: along the rows (the axis tilt) and across them.
      axisTilt: axisTiltFor({ slopeTilt: supportTilt, slopeAzimuth: supportAzimuth, axisAzimuth }),
      crossAxisSlope: crossAxisSlopeFor({
        slopeTilt: supportTilt,
        slopeAzimuth: supportAzimuth,
        axisAzimuth,
      }),
      // The parcel, when the layout was fitted to one: the tables as segments
      // in a local east/north frame in metres, and the boundary rings in the
      // same frame. Recentred on the tables so the camera, which orbits the
      // origin, looks at the array and not at the parcel's centroid.
      parcel: parcelFrame(parcelTables, parcelOutline),
      arrayOffset: { eastM: Number(arrayOffsetEast) || 0, northM: Number(arrayOffsetNorth) || 0 },
      // The ground plane, resolved once. Everything that has to sit on the
      // terrain reads it from here - the rows, the shadows they cast, and the
      // imported model - so there is only one definition of where the ground is.
      terrain: buildTerrain(supportTilt, supportAzimuth),
      // The REAL field, as opposed to the slice on screen. An obstacle shades
      // some rows and not others, so "what fraction of the array" depends on
      // how big the array is - and until now it was measured across whatever
      // the Rows dropdown happened to be showing, which made a drawing choice
      // move a number the simulation uses. Taken from the module count, the
      // modules stacked per row, and the number of rows the layout says the
      // field has. Where the layout does not say, the count that makes the
      // field square is assumed:
      //     N = rows x along x high,  rows x pitch ~ along x moduleWidth
      //   → rows = sqrt(N x moduleWidth / (pitch x high))
      field: deriveField({
        fieldRows,
        rowModules,
        totalModules, pitch: p, moduleWidth: alongRow,
        verticalModules: nV,
      }),
      module: {
        portrait: portrait !== false,
        halfCut: halfCut !== false,
        bypassDiodes: Number(bypassDiodes) || 3,
      },
      model: nearShadingModel || "bypass",
      electricalEffect:
        electricalEffect == null ? 1 : Number(electricalEffect),
    };
}
